import { useRef } from "react";
import { useEffect, useMemo, useState, useCallback } from "react";
import { Spin, Table, Input, Checkbox, Empty } from "antd";
import LoadingOverlay from "../shared/LoadingOverlay";
import Toast from "../shared/Toast";
import {
    FIELD_IDS,
    toNumber,
    money,
    buildVODGridRows,
    buildGroupedVodRows,
    normalizeYesNo,
    getProgramConfig,
    isIncomeTypeAllowed,
    isCliProgram,
    calcAgeYearsFromBirthDate
} from "../../utils/assetDepletionHelper";
import { formatCurrencyTyping, enforceNonNegativeCurrency } from "../../utils/format";
import { getAccessToken } from "../../services/authApi";
import { getBorrowerPairs } from "../../services/borrowerApi";
import { hasPageAccess } from "../../services/personaApi";
import { fetchVODs, updateVods, fetchGiftsGrants } from "../../services/loanSubCollectionApi";
import { fieldReader as fieldReaderApi, fieldWriter as fieldWriterApi } from "../../services/loanApi";
import { generateAssetDepletionPdf } from "../../services/internalApi";
import {
    recalcReservesFromData,
} from "../../utils/requiredReservesHelper";
import ConfirmPrompt from "../shared/ConfirmPrompt";
import { LOAN_PROGRAM_CODES } from "../../config/organizations";
import {
    getLoanProgramLabel,
    getLoanProgramTitle,
    isLoanProgram
} from "../../utils/loanProgramUtil";

export default function AssetDepletionPage() {
    const [loanId, setLoanId] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");

    // read-only field values
    const [fieldValues, setFieldValues] = useState({
        loanProgram: "",
        assets1547: "",
        cashFromToBorrower142: "",
        verifiedAsset: "",
        verifiedAssetReserve: "",
        requiredFundsToClose: "",
        totalVerifiedAssets: "",
        qualifyingPitiaPayment: "",
        monthsReservesRequired: "",
        loanDocType: ""
    });

    // VOD data
    const [applicationId, setApplicationId] = useState("");
    const [vodLoading, setVodLoading] = useState(false);
    const [vodRows, setVodRows] = useState([]);

    const [token, setToken] = useState("");
    const [borrowerPairs, setBorrowerPairs] = useState([]);
    const [selectedPairLegacyId, setSelectedPairLegacyId] = useState("");
    const [selectedPair, setSelectedPair] = useState(null); // store whole selected pair

    const [vodsRaw, setVodsRaw] = useState([]);
    const [savedVodConfig, setSavedVodConfig] = useState(null); // parsed CX.ASSET.DEPL.VOD.JSON
    const savedVodConfigRef = useRef(null);
    const vodLoadInFlightRef = useRef(false);
    const [isSuperJumbo, setIsSuperJumbo] = useState(false);
    const [loanNumber, setLoanNumber] = useState("");
    const amountInputRefs = useRef({});
    const [toastAnimation, setToastAnimation] = useState("animate-fade-in-right");

    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("success");
    const [showToast, setShowToast] = useState(false);
    const [showRemoveAuConfirm, setShowRemoveAuConfirm] = useState(false);

    // Eligibility gating state
    const [eligibilityChecked, setEligibilityChecked] = useState(false);
    const [isEligibleForAssetDepl, setIsEligibleForAssetDepl] = useState(false);
    const [eligibilityMessage, setEligibilityMessage] = useState("");
    const [programCalcType, setProgramCalcType] = useState(""); // "DTI" | "DSCR" | ""
    const [programMonths, setProgramMonths] = useState(60); // dynamic per config (default 60)

    // values required for dynamic rules
    const [loanProgram1401, setLoanProgram1401] = useState(""); // Field 1401 (must be not empty)
    const [loanProgramCode, setLoanProgramCode] = useState(""); // CX.LOAN.PROGRAM.IDENTIFIER
    const [shortTermRental, setShortTermRental] = useState(""); // CX.Short.Term.Rental
    const [selectedProduct, setSelectedProduct] = useState(""); // CX.LOAN.PRODUCT.NAME
    const [loanProductName, setLoanProductName] = useState("");

    const selectedProductRef = useRef("");

    const [canSaveAuIncome, setCanSaveAuIncome] = useState(false);
    const [permissionMessage, setPermissionMessage] = useState("");

    const [dscrBlocked, setDscrBlocked] = useState(false);
    const [dscrBlockedMessage, setDscrBlockedMessage] = useState("");

    // DSCR display + calc inputs (read-only source fields)
    const [dscrFields, setDscrFields] = useState({
        dscrCalculated: "",
        nonq22: "",
        nonq23: "",
        nonq24: "",
        nonq25: "",
        nonq26: "",
        nonq27: "",
        nonq28: "",
        nonq29: ""
    });

    const DSCR_PROGRAMS = ["BPACC", "BITIN", "BAMFN", "SJBPACC"];

    const showDscrSection = useMemo(() => {
        const code = String(loanProgramCode || "").trim().toUpperCase();
        return programCalcType === "DSCR" && DSCR_PROGRAMS.includes(code);
    }, [programCalcType, loanProgramCode]);

    const isDscrProgram = useMemo(() => {
        const code = String(loanProgramCode || "").trim().toUpperCase();
        return programCalcType === "DSCR" && DSCR_PROGRAMS.includes(code);
    }, [programCalcType, loanProgramCode]);

    const dscrRatioNum = useMemo(() => {
        return toNumber(dscrFields.dscrCalculated);
    }, [dscrFields.dscrCalculated]);

    const isDscrBlockedNow = useMemo(() => {
        return isDscrProgram && dscrRatioNum > 1;
    }, [isDscrProgram, dscrRatioNum]);
    const [noVerifiedAssets, setNoVerifiedAssets] = useState(false);

    const showToastMsg = (message, type = "success") => {
        setToastMessage(message);
        setToastType(type);
        setShowToast(true);
    };

    useEffect(() => {
        if (!showToast) return;

        setToastVisible(true);
        setToastAnimation("animate-fade-in-right");

        const hideTimer = setTimeout(() => {
            setToastAnimation("animate-fade-out-right");

            const vanishTimer = setTimeout(() => {
                setShowToast(false);
                setToastVisible(false);
            }, 500);

            return () => clearTimeout(vanishTimer);
        }, 4000);

        return () => clearTimeout(hideTimer);
    }, [showToast]);

    // Read loanId on mount
    useEffect(() => {
        const fetchLoan = async () => {
            try {
                if (window.elli?.script?.guest) {
                    window.elli.script.guest.create();
                    const loanObject = await window.elli.script.getObject("loan");
                    const loan = await loanObject.all();
                    const id = loan?.loan?.id;
                    setLoanId(id || "");

                    const number = loan?.loan?.loanNumber;
                    setLoanNumber(number || "");
                } else {
                    console.warn("elli.script not available");
                }
            } catch (err) {
                console.error("Failed to read loan id from elli script", err);
            }

            try {
                const authObj = await window.elli.script.getObject("auth");
                const user = await authObj.getUser();

                const allowed = await hasPageAccess("AssetDepletionPage", user);

                setCanSaveAuIncome(allowed);

                if (!allowed) {
                    setPermissionMessage("You don’t have permission to save AU Income. Please contact an Underwriter / Super Administrator.");
                } else {
                    setPermissionMessage("");
                }
            } catch (err) {
                console.warn("Failed to read auth user:", err);
                setCanSaveAuIncome(false);
                setPermissionMessage("You don’t have permission to save AU Income.");
            }

        };
        fetchLoan();
    }, []);

    // Eligibility evaluation
    const evaluateEligibility = useCallback((fieldsMap) => {
        // 1401 check first
        const lp1401 = String(fieldsMap[FIELD_IDS.loanProgram_1401] ?? "").trim();
        setLoanProgram1401(lp1401);

        // Program code (CX.LOAN.PROGRAM.IDENTIFIER)
        const progCode = String(fieldsMap[FIELD_IDS.loanProgram] ?? "").trim();
        const progCodeU = progCode.toUpperCase();
        setLoanProgramCode(progCode);

        // STR field
        const strVal = normalizeYesNo(fieldsMap[FIELD_IDS.shortTermRental]);
        setShortTermRental(strVal);

        // Select Product (used inside VOD grid build rules)
        const selectProd = String(fieldsMap[FIELD_IDS.loanProductName] ?? "").trim();
        selectedProductRef.current = selectProd;   // immediate, not async
        setSelectedProduct(selectProd);


        // If loan program (1401) is empty => block
        if (!lp1401) {
            setEligibilityChecked(true);
            setIsEligibleForAssetDepl(false);
            setEligibilityMessage("Asset Depletion is not allowed when loan program is empty.");
            setProgramCalcType("");
            setProgramMonths(60);
            return;
        }

        // CLI loans => block
        if (isCliProgram(progCodeU)) {
            setEligibilityChecked(true);
            setIsEligibleForAssetDepl(false);
            setEligibilityMessage("Asset Depletion is not allowed for this loan program.");
            setProgramCalcType("");
            setProgramMonths(60);
            return;
        }

        // find config row
        const cfg = getProgramConfig(progCodeU);

        // If not found in config => treat as not eligible
        if (!cfg) {
            setEligibilityChecked(true);
            setIsEligibleForAssetDepl(false);
            setEligibilityMessage("Asset Depletion is not allowed for this loan program.");
            setProgramCalcType("");
            setProgramMonths(60);
            return;
        }

        // If disallowed by program
        if (!cfg.allowed) {
            setEligibilityChecked(true);
            setIsEligibleForAssetDepl(false);
            setEligibilityMessage("Asset Depletion is not allowed for this loan program.");
            setProgramCalcType("");
            setProgramMonths(cfg.months || 60);
            return;
        }

        // BPACC + STR yes => not allowed
        if (cfg.strDisallow && strVal === "YES") {
            setEligibilityChecked(true);
            setIsEligibleForAssetDepl(false);
            setEligibilityMessage("Asset Depletion is not allowed for this loan program.");
            setProgramCalcType("");
            setProgramMonths(cfg.months || 60);
            return;
        }

        // Program restricted to specific income types (e.g. CACTTX6 is
        // bank-statement only) => block anything else
        if (!isIncomeTypeAllowed(cfg, fieldsMap[FIELD_IDS.incomeType])) {
            setEligibilityChecked(true);
            setIsEligibleForAssetDepl(false);
            setEligibilityMessage("Asset Depletion is not allowed for this income type.");
            setProgramCalcType("");
            setProgramMonths(cfg.months || 60);
            return;
        }

        // Eligible
        setEligibilityChecked(true);
        setIsEligibleForAssetDepl(true);
        setEligibilityMessage("");
        setProgramCalcType(cfg.calcType === "DTI" || cfg.calcType === "DSCR" ? cfg.calcType : "");
        setProgramMonths(Number.isFinite(Number(cfg.months)) ? Number(cfg.months) : 60);
    }, []);

    const fetchReadOnlyFields = useCallback(async (token, theLoanId) => {
        try {
            const payload = [
                FIELD_IDS.loanProgram,             // CX.LOAN.PROGRAM.IDENTIFIER

                FIELD_IDS.totalVerifiedAssets,     // 1547 (Assets)
                FIELD_IDS.cashFromToBorrower,      // 142

                FIELD_IDS.qualifyingPitiaPayment,  // 912

                FIELD_IDS.verifiedAsset,           // CX.VERIFIED.ASSET
                FIELD_IDS.verifiedAssetReserve,    // CX.VERIFIED.ASSET.RESERVE

                // keep the rest as you already have (eligibility + json + dscr fields etc.)
                FIELD_IDS.monthsReservesRequired,
                FIELD_IDS.assetDeplVodJson,
                FIELD_IDS.assetUtilIncomeTotal,
                FIELD_IDS.docType,
                FIELD_IDS.incomeType,
                FIELD_IDS.loanProductName,
                FIELD_IDS.loanAmount,
                FIELD_IDS.loanProgram_1401,
                FIELD_IDS.shortTermRental,
                FIELD_IDS.dscrCalculated,
                FIELD_IDS.nonq22,
                FIELD_IDS.nonq23,
                FIELD_IDS.nonq24,
                FIELD_IDS.nonq25,
                FIELD_IDS.nonq26,
                FIELD_IDS.nonq27,
                FIELD_IDS.nonq28,
                FIELD_IDS.nonq29
            ];


            const data = await fieldReaderApi(token, payload, theLoanId);
            const fieldsMap = data?.fields || data || {};

            const jsonText = fieldsMap[FIELD_IDS.assetDeplVodJson] ?? "";

            let parsed = null;
            try {
                parsed = jsonText ? JSON.parse(jsonText) : null;
            } catch {
                parsed = null;
            }

            setSavedVodConfig(parsed);
            savedVodConfigRef.current = parsed;

            const productName = String(fieldsMap[FIELD_IDS.loanProductName] ?? "");

            // loanProductName is only ever shown to the user or sent to the
            // PDF, so hold this org's product name rather than the canonical
            // code. Every comparison below works off `productName`.
            setLoanProductName(getLoanProgramLabel(productName) || productName);

            const loanAmt = toNumber(fieldsMap[FIELD_IDS.loanAmount]);

            // SUPER JUMBO / STARRY, matched on the canonical code rather than
            // on a brand name that only exists in one company's Encompass.
            const superJumbo = isLoanProgram(productName, LOAN_PROGRAM_CODES.PROGRAM_008);

            setIsSuperJumbo(superJumbo);

            let loanProgramDisplay = fieldsMap[FIELD_IDS.loanProgram] ?? "";

            if (superJumbo) {
                // Title-cased org product name ("Super Jumbo" / "Starry") so
                // the label follows the brand rather than hardcoding Champions'.
                const superJumboLabel =
                    getLoanProgramTitle(LOAN_PROGRAM_CODES.PROGRAM_008) || "Super Jumbo";

                loanProgramDisplay = loanAmt <= 4000000
                    ? `${superJumboLabel} =< $4M`
                    : `${superJumboLabel} > $4M`;
            }

            setFieldValues({
                loanProgram: loanProgramDisplay ?? "",
                assets1547: fieldsMap[FIELD_IDS.totalVerifiedAssets] ?? "",
                cashFromToBorrower142: fieldsMap[FIELD_IDS.cashFromToBorrower] ?? "",
                verifiedAsset: fieldsMap[FIELD_IDS.verifiedAsset] ?? "",
                verifiedAssetReserve: fieldsMap[FIELD_IDS.verifiedAssetReserve] ?? "",
                requiredFundsToClose: fieldsMap[FIELD_IDS.requiredFundsToClose] ?? "",
                totalVerifiedAssets: fieldsMap[FIELD_IDS.totalVerifiedAssets] ?? "",
                qualifyingPitiaPayment: fieldsMap[FIELD_IDS.qualifyingPitiaPayment] ?? "",
                monthsReservesRequired: fieldsMap[FIELD_IDS.monthsReservesRequired] ?? "",
                loanDocType: fieldsMap[FIELD_IDS.docType] ?? ""
            });

            setDscrFields({
                dscrCalculated: fieldsMap[FIELD_IDS.dscrCalculated] ?? "",
                nonq22: fieldsMap[FIELD_IDS.nonq22] ?? "",
                nonq23: fieldsMap[FIELD_IDS.nonq23] ?? "",
                nonq24: fieldsMap[FIELD_IDS.nonq24] ?? "",
                nonq25: fieldsMap[FIELD_IDS.nonq25] ?? "",
                nonq26: fieldsMap[FIELD_IDS.nonq26] ?? "",
                nonq27: fieldsMap[FIELD_IDS.nonq27] ?? "",
                nonq28: fieldsMap[FIELD_IDS.nonq28] ?? "",
                nonq29: fieldsMap[FIELD_IDS.nonq29] ?? ""
            });

            // evaluate eligibility right after reading fields
            evaluateEligibility(fieldsMap);

            // DSCR hard block (DSCR programs only): if DSCR > 1, do not allow Asset Depletion page to load
            const progCodeU = String(fieldsMap[FIELD_IDS.loanProgram] ?? "").trim().toUpperCase();
            const cfg1 = getProgramConfig(progCodeU);

            const isDscrCfg = cfg1?.calcType === "DSCR" && DSCR_PROGRAMS.includes(progCodeU);
            const dscrVal = toNumber(fieldsMap[FIELD_IDS.dscrCalculated]);

            if (isDscrCfg && dscrVal > 1) {
                const msg = `Asset Depletion isn’t available because your DSCR is ${Number(dscrVal).toFixed(2)} (must be below 1.00).`;
                setDscrBlocked(true);
                setDscrBlockedMessage(msg);

                // Treat as not eligible so init() won't load borrower pairs / VODs
                setEligibilityChecked(true);
                setIsEligibleForAssetDepl(false);
                setEligibilityMessage(msg);

                return false;
            }

            // if not blocked
            setDscrBlocked(false);
            setDscrBlockedMessage("");


            const lp1401 = String(fieldsMap[FIELD_IDS.loanProgram_1401] ?? "").trim();
            const progCode = String(fieldsMap[FIELD_IDS.loanProgram] ?? "").trim().toUpperCase();
            const strVal = String(fieldsMap[FIELD_IDS.shortTermRental] ?? "").trim().toUpperCase();

            const cfg = getProgramConfig(progCode);
            const eligibleNow =
                !!lp1401 &&
                !isCliProgram(progCode) &&
                !!cfg &&
                cfg.allowed &&
                !(cfg.strDisallow && (strVal === "YES" || strVal === "Y" || strVal === "TRUE")) &&
                isIncomeTypeAllowed(cfg, fieldsMap[FIELD_IDS.incomeType]);

            return eligibleNow;


        } catch (err) {
            console.error("FieldReader error:", err);
            showToastMsg("Failed to load read-only fields.", "error");

            // If we fail reading eligibility, treat as blocked to be safe
            setEligibilityChecked(true);
            setIsEligibleForAssetDepl(false);
            setEligibilityMessage("Asset Depletion is not allowed for this loan program.");
            setProgramCalcType("");
            setProgramMonths(60);
        }
    }, [evaluateEligibility]);

    const formatPairLabel = (pair) => {
        return pair?.coborrower?.firstName
            ? `${pair.borrower?.fullName} & ${pair.coborrower?.fullName}`
            : (pair.borrower?.fullName || "");
    };

    const getOwnerAgeYearsMap = (pair) => {
        const bBirth = pair?.borrower?.birthDate || "";
        const cBirth = pair?.coborrower?.birthDate || "";

        return {
            Borrower: calcAgeYearsFromBirthDate(bBirth),
            CoBorrower: calcAgeYearsFromBirthDate(cBirth)
        };
    };

    const getVerifiedReserveKeysForApp = (jsonObj, appId) => {
        if (!jsonObj || typeof jsonObj !== "object") return null;

        const apps = Array.isArray(jsonObj.applications) ? jsonObj.applications : [];
        const app = apps.find(a => a?.applicationId === appId);
        const rows = app?.reserves?.rows;

        if (!Array.isArray(rows) || !rows.length) return null;

        // Only keys with verified === true
        const verifiedKeys = rows
            .filter(r => r?.verified === true && r?.key)
            .map(r => String(r.key).toLowerCase().trim())
            .filter(Boolean);

        // If no verified true => treat as "empty grid"
        if (!verifiedKeys.length) return new Set(); // empty set means show empty grid

        return new Set(verifiedKeys);
    };

    const emptyVerifiedMessage = (
        <Empty
            description={
                <div
                    style={{
                        color: "var(--slate-grey-2)",
                        fontSize: "14px",
                        lineHeight: "1.6"
                    }}
                >
                    <div
                        style={{
                            fontWeight: 700,
                            fontSize: "15px",
                            marginBottom: "2px"
                        }}
                    >
                        No verified asset found.
                    </div>

                    <div style={{ fontWeight: 600 }}>
                        Go to{" "}
                        <span style={{ fontWeight: 700 }}>
                            Required Reserves
                        </span>{" "}
                        screen and select{" "}
                        <span style={{ fontWeight: 700 }}>
                            Verified asset
                        </span>.
                    </div>
                </div>
            }
        />
    );


    const loadVodsForApplication = useCallback(async (token, theLoanId, appId, ownerAgeYearsMap) => {

        if (vodLoadInFlightRef.current) return;
        vodLoadInFlightRef.current = true;

        setVodLoading(true);
        try {
            if (!appId) {
                setVodRows([]);
                return;
            }

            // =========================================================
            // - If CX.ASSET.DEPL.VOD.JSON empty OR no reserves.rows => empty grid
            // - If reserves.rows has verified true => show ONLY those keys
            // - If reserves.rows exists but all verified false => empty grid
            // =========================================================
            const cfg = savedVodConfigRef.current;

            // JSON empty => empty grid
            if (!cfg) {
                setNoVerifiedAssets(true);
                setVodsRaw([]);
                setVodRows([]);
                return;
            }

            const verifiedKeySet = getVerifiedReserveKeysForApp(cfg, appId);

            // missing reserves.rows => empty grid
            if (verifiedKeySet === null) {
                setNoVerifiedAssets(true);
                setVodsRaw([]);
                setVodRows([]);
                return;
            }

            // reserves.rows exists but none verified true => empty grid
            if (verifiedKeySet.size === 0) {
                setNoVerifiedAssets(true);
                setVodsRaw([]);
                setVodRows([]);
                return;
            }
            setNoVerifiedAssets(false);
            // Only now load VODs and filter to verified reserve keys
            const vods = await fetchVODs(token, theLoanId, appId);
            setVodsRaw(Array.isArray(vods) ? vods : []);

            const sel = selectedProductRef.current || "";
            const rows = buildVODGridRows(vods, ownerAgeYearsMap, sel);

            // FILTER: show only accounts that exist in reserves.rows and verified === true
            const filteredRows = (rows || []).filter(r =>
                verifiedKeySet.has(String(r.key || "").toLowerCase().trim())
            );

            // restore useForAus from saved json (if present) -- your existing logic
            const restoredRows = filteredRows.map(r => {
                const cfg2 = savedVodConfigRef.current;
                const app = cfg2?.applications?.find(a => a.applicationId === appId);
                const vodCfg = app?.vods?.find(v => v.id === r.vodId);

                if (!vodCfg) return r;

                return {
                    ...r,
                    useForAus: !!vodCfg.useForAus
                };
            });

            setVodRows(prev => {
                const prevMap = new Map((prev || []).map(x => [x.key, x]));
                return restoredRows.map(r => {
                    const old = prevMap.get(r.key);
                    if (!old) return r;
                    return {
                        ...r,
                        useForAus: old.useForAus ?? r.useForAus,
                        amountUsed: (old.amountUsed ?? "") !== "" ? old.amountUsed : r.amountUsed
                    };
                });
            });

        } catch (err) {
            console.error("VOD load error:", err);
            showToastMsg("Failed to load VODs.", "error");
            setVodsRaw([]);
            setVodRows([]);
        } finally {
            setVodLoading(false);
            vodLoadInFlightRef.current = false;
        }
    }, [selectedProduct]);


    const loadBorrowerPairs = useCallback(async (token, theLoanId) => {
        try {
            const pairs = await getBorrowerPairs(token, theLoanId);
            setBorrowerPairs(Array.isArray(pairs) ? pairs : []);

            if (pairs?.length) {
                const first = pairs[0];
                setSelectedPair(first);
                setSelectedPairLegacyId(first.legacyId || "");
                setApplicationId(first.id || "");
                const ownerAgeYearsMap = getOwnerAgeYearsMap(first);
                await loadVodsForApplication(token, theLoanId, first.id, ownerAgeYearsMap);

            } else {
                setSelectedPair(null);
                setSelectedPairLegacyId("");
                setApplicationId("");
                setVodRows([]);
                showToastMsg("No borrower pairs found.", "error");
            }
        } catch (err) {
            console.error("Borrower pairs load error:", err);
            showToastMsg("Failed to load borrower pairs.", "error");
            setBorrowerPairs([]);
            setSelectedPair(null);
            setSelectedPairLegacyId("");
            setApplicationId("");
            setVodRows([]);
        }
    }, [loadVodsForApplication]);

    // Load everything when loanId is ready
    useEffect(() => {
        if (!loanId) return;

        const init = async () => {
            setLoading(true);
            setLoadingText("Loading Asset Depletion...");
            try {
                const authObj = await window.elli.script.getObject("auth");
                const authCode = await authObj.createAuthCode();
                const t = await getAccessToken(authCode);

                setToken(t);

                // IMPORTANT:
                // fetchReadOnlyFields NOW RETURNS eligibility synchronously
                const eligibleNow = await fetchReadOnlyFields(t, loanId);

                setLoading(false);
                setLoadingText("");

                // Use returned value (NOT React state)
                if (eligibleNow) {
                    await loadBorrowerPairs(t, loanId);
                } else {
                    setBorrowerPairs([]);
                    setSelectedPair(null);
                    setSelectedPairLegacyId("");
                    setApplicationId("");
                    setVodRows([]);
                }

            } catch (err) {
                console.error("Init error:", err);
            } finally {

            }
        };

        init();
    }, [loanId]);

    const totalRequiredReserves = useMemo(() => {
        const pitia = toNumber(fieldValues.qualifyingPitiaPayment);
        const months = toNumber(fieldValues.monthsReservesRequired);
        return pitia * months;
    }, [fieldValues.qualifyingPitiaPayment, fieldValues.monthsReservesRequired]);

    const totalBorrowerFundsNeeded = useMemo(() => {
        const fundsToClose = toNumber(fieldValues.requiredFundsToClose);
        return fundsToClose + totalRequiredReserves;
    }, [fieldValues.requiredFundsToClose, totalRequiredReserves]);

    const onRowUseForAusChange = (rowKey, checked) => {
        setVodRows(prev =>
            prev.map(r =>
                r.key === rowKey
                    ? { ...r, useForAus: checked, amountUsed: checked ? r.amountUsed : "" }
                    : r
            )
        );

        if (checked) {
            requestAnimationFrame(() => {
                amountInputRefs.current[rowKey]?.focus();
            });
        }
    };

    const onRowAmountUsedChange = (rowKey, value) => {
        setVodRows((prev) =>
            prev.map((r) => (r.key === rowKey ? { ...r, amountUsed: value } : r))
        );
    };

    const groupedVodRows = useMemo(
        () => buildGroupedVodRows(vodRows, toNumber),
        [vodRows]
    );

    const totalCashValue = useMemo(() => {
        return vodRows.reduce((sum, r) => sum + toNumber(r.cashValue), 0);
    }, [vodRows]);

    const totalAmountUsed = useMemo(() => {
        return vodRows.reduce((sum, r) => sum + toNumber(r.amountUsed), 0);
    }, [vodRows]);

    const buildVodPatchPayload = (vodsRaw, vodRows) => {
        const rowsToApply = (vodRows || []).filter(
            (r) => !r.isGroup && r.useForAus && toNumber(r.amountUsed) > 0
        );

        const rowsToClear = (vodRows || []).filter((r) => !r.isGroup && !r.useForAus);

        if (!rowsToApply.length && !rowsToClear.length) return [];

        const rowsByVodWrite = new Map();
        rowsToApply.forEach((r) => {
            if (!rowsByVodWrite.has(r.vodId)) rowsByVodWrite.set(r.vodId, []);
            rowsByVodWrite.get(r.vodId).push(r);
        });

        const rowsByVodClear = new Map();
        rowsToClear.forEach((r) => {
            if (!rowsByVodClear.has(r.vodId)) rowsByVodClear.set(r.vodId, []);
            rowsByVodClear.get(r.vodId).push(r);
        });

        const shouldSendUrlaAmount = (item) => {
            const amt = item?.urla2020CashOrMarketValueAmount;

            if (amt === "") return true;
            if (amt === null || amt === undefined) return false;

            const hasKeys = !!(item?.type || item?.accountIdentifier);
            if (!hasKeys && Number(amt) === 0) return false;

            return true;
        };

        const serializeItem = (item) => {
            const out = { itemNumber: item.itemNumber };

            const isExplicitClear = item?.urla2020CashOrMarketValueAmount === "";

            if (isExplicitClear || (item.type !== undefined && item.type !== null && item.type !== ""))
                out.type = item.type ?? "";

            if (isExplicitClear || (item.accountIdentifier !== undefined && item.accountIdentifier !== null && item.accountIdentifier !== ""))
                out.accountIdentifier = item.accountIdentifier ?? "";

            if (isExplicitClear || (item.depositoryAccountName !== undefined && item.depositoryAccountName !== null && item.depositoryAccountName !== ""))
                out.depositoryAccountName = item.depositoryAccountName ?? "";

            if (shouldSendUrlaAmount(item)) {
                out.urla2020CashOrMarketValueAmount = item.urla2020CashOrMarketValueAmount;
            }

            if (Object.keys(out).length === 1) return out;

            return out;
        };

        const vodUpdateArray = [];

        (vodsRaw || []).forEach((vod) => {
            const vodId = vod?.id;
            const writeRows = rowsByVodWrite.get(vodId) || [];
            const clearRows = rowsByVodClear.get(vodId) || [];

            if (!writeRows.length && !clearRows.length) return;

            const items = Array.isArray(vod.items) ? vod.items.map((i) => ({ ...i })) : [];

            clearRows.forEach((row) => {
                const existingNeg = items.find(
                    (i) =>
                        i?.type === row.type &&
                        i?.accountIdentifier === row.accountIdentifier &&
                        toNumber(i?.urla2020CashOrMarketValueAmount) < 0
                );

                if (!existingNeg) return;

                existingNeg.type = "";
                existingNeg.accountIdentifier = "";
                existingNeg.urla2020CashOrMarketValueAmount = "";
                existingNeg.depositoryAccountName = "";
            });

            writeRows.forEach((row) => {
                if (!row.useForAus) return;

                const amount = toNumber(row.amountUsed);
                if (amount <= 0) return;

                const negativeAmount = amount * -1;

                const existingNeg = items.find(
                    (i) =>
                        i?.type === row.type &&
                        i?.accountIdentifier === row.accountIdentifier &&
                        toNumber(i?.urla2020CashOrMarketValueAmount) < 0
                );

                if (existingNeg) {
                    existingNeg.urla2020CashOrMarketValueAmount = negativeAmount;
                    existingNeg.depositoryAccountName =
                        row.depositoryAccountName || existingNeg.depositoryAccountName || "";

                    if (existingNeg._forceClear) delete existingNeg._forceClear;

                    return;
                }

                const emptySlot = items
                    .filter((i) => i?.itemNumber && !i?.type && !i?.accountIdentifier)
                    .sort((a, b) => a.itemNumber - b.itemNumber)[0];

                if (!emptySlot) {
                    console.warn("No empty VOD item slot available to write Amount Used for", vodId, row);
                    return;
                }

                emptySlot.type = row.type;
                emptySlot.accountIdentifier = row.accountIdentifier;
                emptySlot.urla2020CashOrMarketValueAmount = negativeAmount;
                emptySlot.depositoryAccountName = row.depositoryAccountName || "";

                if (emptySlot._forceClear) delete emptySlot._forceClear;
            });

            vodUpdateArray.push({
                id: vodId,
                items: items.map(serializeItem).map((x) => {
                    if (x && typeof x === "object" && "_forceClear" in x) delete x._forceClear;
                    return x;
                })
            });
        });

        return vodUpdateArray;
    };

    const buildAssetDeplVodJson = (existingJson, applicationId, vodRows) => {
        const nowIso = new Date().toISOString();

        const cleanRows = (vodRows || []).filter(r => !r.isGroup);

        const vods = cleanRows.map(r => ({
            id: r.vodId,
            useForAus: !!r.useForAus,
            amountUsed: toNumber(r.amountUsed) > 0 ? toNumber(r.amountUsed) : null
        }));

        const base = existingJson && typeof existingJson === "object"
            ? existingJson
            : { version: 1, lastUpdated: nowIso, applications: [] };

        const apps = Array.isArray(base.applications) ? [...base.applications] : [];
        const idx = apps.findIndex(a => a.applicationId === applicationId);

        const prevApp = idx >= 0 && apps[idx] && typeof apps[idx] === "object"
            ? apps[idx]
            : { applicationId };

        // MERGE: keep prev nodes (like "reserves") and only replace "vods"
        const updatedApp = {
            ...prevApp,
            applicationId,
            vods
        };

        if (idx >= 0) apps[idx] = updatedApp;
        else apps.push(updatedApp);

        return {
            ...base,
            version: base.version || 1,
            lastUpdated: nowIso,
            applications: apps
        };
    };

    async function syncHostLoanWorkspace() {
        try {
            const loanObj = await window.elli.script.getObject("loan");
            await loanObj.merge();
            await loanObj.calculate();
        } catch (e) {
            console.warn("Host loan merge/calc failed (workspace may stay stale until refresh):", e);
        }
    }

    const recalcAndWriteRequiredReserves = async () => {
        if (!token || !loanId || !applicationId) return;

        try {
            // Read fields needed for months calc + JSON + product/program
            const payload = [
                FIELD_IDS.assetDeplVodJson,
                FIELD_IDS.cashInHand_142, // 142
                FIELD_IDS.ltv_353,        // 353
                FIELD_IDS.pitia_912,      // 912
                FIELD_IDS.loanProgram,    // CX.LOAN.PROGRAM.IDENTIFIER
                FIELD_IDS.loanProductName // CX.LOAN.PRODUCT.NAME
            ];

            const data = await fieldReaderApi(token, payload, loanId);
            const fieldsMap = data?.fields || data || {};

            const jsonText = fieldsMap[FIELD_IDS.assetDeplVodJson] ?? "";
            let parsed = null;
            try {
                parsed = jsonText ? JSON.parse(jsonText) : null;
            } catch {
                parsed = null;
            }

            const cash142 = toNumber(fieldsMap[FIELD_IDS.cashInHand_142]);
            const ltv353 = toNumber(fieldsMap[FIELD_IDS.ltv_353]);
            const pitia912 = toNumber(fieldsMap[FIELD_IDS.pitia_912]);
            const programCode = String(fieldsMap[FIELD_IDS.loanProgram] ?? "").trim();
            const productName = String(fieldsMap[FIELD_IDS.loanProductName] ?? "").trim();

            // Pull latest VODs + Gifts (because AD save/remove updates VOD negatives)
            const [vodsLatest, giftsLatest] = await Promise.all([
                fetchVODs(token, loanId, applicationId),
                fetchGiftsGrants(token, loanId, applicationId)
            ]);

            const result = recalcReservesFromData({
                vodsRaw: Array.isArray(vodsLatest) ? vodsLatest : [],
                giftsRaw: Array.isArray(giftsLatest) ? giftsLatest : [],
                selectedPair,               // already in AD page state
                cash142,
                selectedProduct: productName,
                applicationId,
                savedJsonObj: parsed,
                ltv353,
                pitia912,
                loanProgramCode: programCode
            });

            const totals = result?.totals || {};
            const months = result?.months || { actual: "", noCashout: "" };

            const writerPayload = [
                { id: FIELD_IDS.verifiedAsset, value: String(toNumber(totals.verifiedAsset)) },
                { id: FIELD_IDS.verifiedAssetReserve, value: String(toNumber(totals.verifiedAssetReserve)) },
                { id: FIELD_IDS.verifiedAssetDownPayment, value: String(toNumber(totals.verifiedAssetDownPayment)) },
                { id: FIELD_IDS.reservesActual, value: months.actual },         // "" if not allowed
                { id: FIELD_IDS.reservesNoCashout, value: months.noCashout }    // always rounded 1 dec in calc
            ];

            await fieldWriterApi(token, loanId, writerPayload);

            // Refresh your read-only header fields so Verified Asset Reserve updates on screen
            await fetchReadOnlyFields(token, loanId);
        } catch (e) {
            console.warn("Required Reserves recalc/write failed:", e);
            // Don't block AD save/remove; just inform
            showToastMsg("Saved, but Required Reserves fields failed to recalculate.", "error");
        }
    };

    const totalAmountUsedForAus = useMemo(() => {
        return vodRows
            .filter(r => !r.isGroup && r.useForAus)
            .reduce((s, r) => s + toNumber(r.amountUsed), 0);
    }, [vodRows]);

    // AU Monthly Income uses programMonths (dynamic) — not hard-coded 60/84
    const auMonthlyIncome = useMemo(() => {
        const denom = Number(programMonths) || 0;
        const amt = toNumber(totalAmountUsedForAus);
        return denom ? (amt / denom) : 0;
    }, [totalAmountUsedForAus, programMonths]);

    // CX.NONQ.47 = SumAny(CX.NONQ.22..29, AU Monthly Income)
    const nonq47Client = useMemo(() => {
        return (
            toNumber(auMonthlyIncome)
        );
    }, [auMonthlyIncome]);


    const dscrRatio = useMemo(() => {
        return toNumber(dscrFields.dscrCalculated);
    }, [dscrFields.dscrCalculated]);


    // CX.NONQ.48 = CX.NONQ.47 / 912
    const dscrWithAssetDeplClient = useMemo(() => {
        const pitia = toNumber(fieldValues.qualifyingPitiaPayment);
        if (!pitia) return 0;
        return (toNumber(dscrFields.dscrCalculated) + (toNumber(auMonthlyIncome) / pitia));
    }, [dscrFields.dscrCalculated, fieldValues.qualifyingPitiaPayment, auMonthlyIncome]);

    const dscrRatioIsBad = useMemo(() => showDscrSection && dscrRatio > 1, [showDscrSection, dscrRatio]);
    const dscrWithADIsBad = useMemo(() => {
        if (!showDscrSection) return false;

        const v = toNumber(dscrWithAssetDeplClient);

        // red if < 1.00 OR > 1.30
        return v > 0 && (v < 1 || v > 1.3);
    }, [showDscrSection, dscrWithAssetDeplClient]);

    const buildAssetDepletionPdfPayload = () => {

        // 1) Fields (match screenshot labels)
        const fields = [
            { name: "Loan Program", value: fieldValues.loanProgram ?? "" },
            { name: "Assets", value: fieldValues.assets1547 ?? "" },
            { name: "Cash From/To Borrower", value: fieldValues.cashFromToBorrower142 ?? "" },
            { name: "(P)ITIA", value: fieldValues.qualifyingPitiaPayment ?? "" },
            { name: "Verified Assets", value: fieldValues.verifiedAsset ?? "" },
            { name: "Verified Asset for Reserves", value: fieldValues.verifiedAssetReserve ?? "" }
        ];

        // 2) Grid rows (send flat rows; server will group)
        const rows = (vodRows || [])
            .filter(r => !r.isGroup && !r.isGroupSubtotal) // your vodRows are already flat, but safe
            .map(r => ({
                key: r.key,
                groupName: r.groupName,
                groupInfo: r.groupInfo || "", // optional (server can compute from allowedPercent)
                accountType: r.typeLabel,
                holder: r.holderName,
                accountOwner: r.depositoryAccountName,
                accountNumber: r.accountIdentifier,
                cashValue: String(r.cashValue ?? ""),
                useForAu: !!r.useForAus,
                amountUsed: String(r.amountUsed ?? ""),
                allowedPercent: Number(r.allowedPercent ?? 0) // used for group info like "Max usable 80%"
            }));

        // 3) Totals
        const totals = {
            totalCashValue: String(totalCashValue ?? ""),
            totalAmountUsed: String(totalAmountUsed ?? ""),
            amountUsedByUw: String(totalAmountUsedForAus ?? ""),
            auMonthlyIncome: String(auMonthlyIncome ?? ""),
            programMonths: Number(programMonths || 0),
            loanProgramCode: loanProgramCode || "",
            loanProductName: loanProductName || "",
            ShowDscrSection: !!showDscrSection,
            DscrRatio: dscrRatio ? String(Number(dscrRatio).toFixed(3)) : "",
            DscrWithAssetDepl: dscrWithAssetDeplClient ? String(Number(dscrWithAssetDeplClient).toFixed(3)) : "",
        };

        return { Fields: fields, Rows: rows, Totals: totals };
    };

    const handleGenerateAssetDepletionPdf = async () => {
        try {
            setLoading(true);
            setLoadingText("Saving Asset Depletion & uploading PDF...");

            const authObj = await window.elli.script.getObject("auth");
            const authCode = await authObj.createAuthCode();

            const payload = buildAssetDepletionPdfPayload();

            // Call API (returns blob) but DO NOT download it.
            // Just await it so the server can render + upload into eFolder.
            await generateAssetDepletionPdf(authCode, loanId, loanNumber, payload);

            showToastMsg("Asset Depletion saved and PDF uploaded to eFolder.", "success");
        } catch (e) {
            console.error("Asset Depletion PDF upload failed:", e);
            showToastMsg("Saved, but PDF upload failed.", "error");
        } finally {
            setLoading(false);
            setLoadingText("");
        }
    };

    const onSave = async () => {
        if (!token || !loanId || !applicationId) {
            showToastMsg("Missing token/loan/application.", "error");
            return;
        }

        if (!canSaveAuIncome) {
            showToastMsg(permissionMessage || "You don’t have permission to save AU Income.", "error");
            return;
        }

        // Block save if not eligible (button already disabled, but safe)
        if (!isEligibleForAssetDepl) {
            showToastMsg(eligibilityMessage || "Asset Depletion is not allowed for this loan program.", "error");
            return;
        }

        // Must have calc type from config
        if (programCalcType !== "DTI" && programCalcType !== "DSCR") {
            showToastMsg("Asset Depletion is not allowed for this loan program.", "error");
            return;
        }

        const invalidRows = (vodRows || []).filter(r => {
            if (r.isGroup) return false;
            if (!r.useForAus) return false;

            const amt = toNumber(r.amountUsed);
            return !amt || amt <= 0;
        });

        if (invalidRows.length) {
            showToastMsg("Enter Amount Used for all checked rows (Use for AU?).", "error");
            return;
        }

        // DSCR validations (block save)
        if (programCalcType === "DSCR" && DSCR_PROGRAMS.includes(String(loanProgramCode || "").toUpperCase())) {
            if (dscrRatio > 1) {
                showToastMsg("DSCR Ratio must be <= 1.00 to save AU Monthly Income.", "error");
                return;
            }
            const dscrWithAD = toNumber(dscrWithAssetDeplClient);

            if (dscrWithAD > 0 && (dscrWithAD < 1 || dscrWithAD > 1.3)) {
                showToastMsg("DSCR Ratio w/Asset Depletion must be between 1.00 and 1.30.", "error");
                return;
            }

        }

        try {
            setLoading(true);
            setLoadingText("Saving Asset Depletion...");

            const vodPatchPayload = buildVodPatchPayload(vodsRaw, vodRows);
            if (vodPatchPayload.length) {
                await updateVods(token, loanId, applicationId, vodPatchPayload);
            }

            const jsonObj = buildAssetDeplVodJson(savedVodConfig, applicationId, vodRows);
            const jsonText = JSON.stringify(jsonObj);

            const monthlyIncomeNum = toNumber(auMonthlyIncome);
            const monthlyIncomeVal = monthlyIncomeNum > 0 ? String(monthlyIncomeNum) : "";

            // write based on programCalcType (DTI vs DSCR), not docType
            let writerPayload = [];

            if (programCalcType === "DSCR") {
                writerPayload = [
                    { id: FIELD_IDS.assetDeplVodJson, value: jsonText },
                    { id: FIELD_IDS.assetUtilIncomeTotal, value: monthlyIncomeVal }, // DSCR => CX.NONQ.46
                    { id: FIELD_IDS.additionalIncomeDescription, value: "" },        // clear 1818
                    { id: FIELD_IDS.incomeBorrOtherIncome, value: "" }               // clear 1815
                ];
            } else {
                // DTI
                writerPayload = [
                    { id: FIELD_IDS.assetDeplVodJson, value: jsonText },
                    { id: FIELD_IDS.additionalIncomeDescription, value: "AU" },       // 1818 = "AU"
                    { id: FIELD_IDS.incomeBorrOtherIncome, value: monthlyIncomeVal }, // 1815 = AU Monthly Income
                    { id: FIELD_IDS.assetUtilIncomeTotal, value: "" }                 // clear CX.NONQ.46
                ];
            }

            await fieldWriterApi(token, loanId, writerPayload);
            await recalcAndWriteRequiredReserves();

            await syncHostLoanWorkspace();

            savedVodConfigRef.current = jsonObj;
            setSavedVodConfig(jsonObj);

            // generate + upload PDF
            await handleGenerateAssetDepletionPdf();

            showToastMsg("Asset Depletion details saved successfully.", "success");

        } catch (err) {
            console.error("Save failed:", err);

            let msg =
                err?.response?.data?.details ||
                err?.response?.data?.summary ||
                err?.message ||
                "Save failed.";

            if (loanNumber) {
                msg = msg.replace(
                    /Loan\s+'[a-f0-9-]+'/i,
                    `Loan '${loanNumber}'`
                );
            }

            showToastMsg(msg, "error");
        }
        finally {
            setLoading(false);
            setLoadingText("");
        }
    };

    const handleRemoveAdIncome = async () => {
        if (!token || !loanId) {
            showToastMsg("Missing token/loan.", "error");
            return;
        }

        if (!canSaveAuIncome) {
            showToastMsg(permissionMessage || "You don’t have permission to remove AU Income.", "error");
            return;
        }

        try {
            setLoading(true);
            setLoadingText("Removing Asset Depletion income...");

            const hasAnyGridRows =
                Array.isArray(vodRows) && vodRows.some(r => !r.isGroup && !r.isGroupSubtotal);

            if (hasAnyGridRows && applicationId) {
                // 1) Uncheck + clear amounts in UI
                const clearedRows = (vodRows || []).map(r => {
                    if (r.isGroup || r.isGroupSubtotal) return r;
                    return { ...r, useForAus: false, amountUsed: "" };
                });

                setVodRows(clearedRows);

                // 2) Re-fetch VODs FIRST so we can see the negative rows we must clear
                const latestVods = await fetchVODs(token, loanId, applicationId);
                setVodsRaw(Array.isArray(latestVods) ? latestVods : []);

                // 3) Clear negative VOD entries (Amount Used) using latest VOD snapshot
                const vodPatchPayload = buildVodPatchPayload(latestVods, clearedRows);
                if (vodPatchPayload.length) {
                    await updateVods(token, loanId, applicationId, vodPatchPayload);
                }

                // 4) Update JSON so it won’t restore checked rows next load
                const jsonObj = buildAssetDeplVodJson(savedVodConfigRef.current, applicationId, clearedRows);
                const jsonText = JSON.stringify(jsonObj);

                // 5) Clear AU Monthly Income fields (DTI vs DSCR safe clear)
                // - DSCR uses CX.NONQ.46
                // - DTI uses 1815/1818
                // We clear ALL to avoid stale calc on either path.
                await fieldWriterApi(token, loanId, [
                    { id: FIELD_IDS.assetDeplVodJson, value: jsonText },

                    // DSCR field
                    { id: FIELD_IDS.assetUtilIncomeTotal, value: "" }, // CX.NONQ.46

                    // DTI fields
                    { id: FIELD_IDS.additionalIncomeDescription, value: "" }, // 1818
                    { id: FIELD_IDS.incomeBorrOtherIncome, value: "" }        // 1815
                ]);

                savedVodConfigRef.current = jsonObj;
                setSavedVodConfig(jsonObj);
            } else {
                // Grid empty -> just clear income fields
                await fieldWriterApi(token, loanId, [
                    { id: FIELD_IDS.assetUtilIncomeTotal, value: "" },         // CX.NONQ.46
                    { id: FIELD_IDS.additionalIncomeDescription, value: "" },  // 1818
                    { id: FIELD_IDS.incomeBorrOtherIncome, value: "" }         // 1815
                ]);
            }

            // OPTIONAL: if your recalc reads sub-collections immediately,
            // keeping a tiny delay can reduce “eventual consistency” issues.
            // But the real fix is "fetch latestVods before patch".
            await new Promise(resolve => setTimeout(resolve, 300));

            // If you have this function, call it here (it should read fresh fields/VODs internally)
            await recalcAndWriteRequiredReserves();

            // Refresh Encompass workspace
            await syncHostLoanWorkspace();

            showToastMsg("Asset Depletion income cleared.", "success");
        } catch (err) {
            console.error("Remove AD Income failed:", err);

            let msg =
                err?.response?.data?.details ||
                err?.response?.data?.summary ||
                err?.message ||
                "Remove AD Income failed.";

            if (loanNumber) {
                msg = msg.replace(/Loan\s+'[a-f0-9-]+'/i, `Loan '${loanNumber}'`);
            }

            showToastMsg(msg, "error");
        } finally {
            setLoading(false);
            setLoadingText("");
        }
    };

    window.addEventListener("beforeunload", async () => {
        try {
            const loanObj = await window.elli.script.getObject("loan");
            await loanObj.merge();
        } catch { }
    });

    const onBlockedMessage = useMemo(() => {
        if (!eligibilityChecked) return "";
        if (dscrBlocked) return dscrBlockedMessage;
        if (isEligibleForAssetDepl) return "";
        return eligibilityMessage || "Asset Depletion is not allowed for this loan program.";
    }, [eligibilityChecked, dscrBlocked, dscrBlockedMessage, isEligibleForAssetDepl, eligibilityMessage]);

    const showBlockedScreen = useMemo(() => {
        if (!eligibilityChecked) return false;
        if (dscrBlocked) return true;
        return !isEligibleForAssetDepl;
    }, [eligibilityChecked, dscrBlocked, isEligibleForAssetDepl]);


    const columns = [
        {
            title: "Account Type",
            dataIndex: "typeLabel",
            key: "typeLabel",
            width: "28%",
            render: (val, row) => {
                if (row.isGroup) {
                    return {
                        children: (
                            <span>
                                {val}
                                {row.groupInfo ? (
                                    <span className="group-inline-info">
                                        {" "} {row.groupInfo}
                                    </span>
                                ) : null}
                            </span>
                        ),
                        props: { colSpan: 7 }
                    };
                }

                // subtotal row label spans first 4 cols to look like a footer
                if (row.isGroupSubtotal) {
                    return {
                        children: <span>Subtotal</span>,
                        props: { colSpan: 4 }
                    };
                }

                return val;
            }

        },
        {
            title: "Holder",
            dataIndex: "holderName",
            key: "holderName",
            width: "14%",
            render: (val, row) => {
                if (row.isGroup) return { children: null, props: { colSpan: 0 } };
                if (row.isGroupSubtotal) return { children: null, props: { colSpan: 0 } };
                return val || "";
            }

        },
        {
            title: "Account Owner",
            dataIndex: "depositoryAccountName",
            key: "depositoryAccountName",
            width: "16%",
            render: (val, row) => {
                if (row.isGroup) return { children: null, props: { colSpan: 0 } };
                if (row.isGroupSubtotal) return { children: null, props: { colSpan: 0 } };
                return val || "";
            }
        },
        {
            title: "Account Number",
            dataIndex: "accountIdentifier",
            key: "accountIdentifier",
            width: "12%",
            render: (val, row) => {
                if (row.isGroup) return { children: null, props: { colSpan: 0 } };
                if (row.isGroupSubtotal) return { children: null, props: { colSpan: 0 } };
                return val || "";
            }
        },
        {
            title: "Cash Or Market Value",
            dataIndex: "cashValue",
            key: "cashValue",
            width: "12%",
            align: "left",
            render: (val, row) => {
                if (row.isGroup) return ``;

                // subtotal row – show summed cash value
                if (row.isGroupSubtotal) return `$ ${money(row.cashValue)}`;

                return `$ ${money(val)}`;
            }
        },
        {
            title: "Use for AU?",
            dataIndex: "useForAus",
            key: "useForAus",
            width: "8%",
            align: "center",
            render: (_, row) => {
                if (row.isGroup || row.isGroupSubtotal) return null;

                const pct = Number(row.allowedPercent ?? 0);
                if (!Number.isFinite(pct) || pct <= 0) return null;

                return (
                    <Checkbox
                        checked={!!row.useForAus}
                        onChange={(e) => onRowUseForAusChange(row.key, e.target.checked)}
                    />
                );
            }

        },
        {
            title: "Amount Used",
            dataIndex: "amountUsed",
            key: "amountUsed",
            width: "10%",
            render: (_, row) => {
                if (row.isGroup) return ``;

                // subtotal row just prints the totals
                if (row.isGroupSubtotal) return `$ ${money(row.amountUsed)}`;

                const pct = Number(row.allowedPercent ?? 0);
                const pctUsable = Number.isFinite(pct) && pct > 0;

                return (
                    <div className="amount-input-wrapper amount-left">
                        <span className="currency-symbol">$</span>
                        <Input
                            ref={(el) => {
                                if (el) amountInputRefs.current[row.key] = el;
                            }}
                            value={row.amountUsed}
                            disabled={!row.useForAus || !pctUsable}
                            onChange={(e) => {
                                const typed = enforceNonNegativeCurrency(e.target.value);
                                const typedNum = toNumber(typed);
                                const max = toNumber(row.allowedMax || (toNumber(row.cashValue)));

                                if (typedNum > max) {
                                    onRowAmountUsedChange(row.key, formatCurrencyTyping(String(max)));
                                    showToastMsg(`Amount Used cannot exceed Cash Value ($ ${money(max)}).`, "error");
                                    return;
                                }

                                onRowAmountUsedChange(row.key, typed);
                            }}
                            onBlur={(e) => {
                                const finalVal = enforceNonNegativeCurrency(e.target.value, true);
                                let num = toNumber(finalVal);
                                const max = toNumber(row.allowedMax || (toNumber(row.cashValue)));

                                if (num > max) {
                                    num = max;
                                    showToastMsg(`Amount Used cannot exceed allowed max ($ ${money(max)}).`, "error");
                                }

                                onRowAmountUsedChange(row.key, num ? money(num) : "");
                            }}
                            placeholder="0.00"
                            className="form-input w-full text-sm border rounded p-1"
                        />
                    </div>
                );
            }

        }
    ];

    return (
        <div className="rr-page space-y-6 p-25px">
            <LoadingOverlay loading={loading} text={loadingText} />

            <div className="rounded-lg p-4 space-y-6 max-w-8xl mx-auto">
                <div className="flex items-center justify-between">
                    <div className="page-title1">Asset Depletion</div>

                    <div className="flex items-center gap-3">
                        {/* ONLY show hint + button when page is eligible */}
                        {!showBlockedScreen && (
                            <>
                                <div className="save-hint">
                                    {canSaveAuIncome ? (
                                        <>
                                            Click <b>Calculate &amp; Save</b> to store <b>AU Monthly Income</b>
                                        </>
                                    ) : (
                                        <span className="text-red-600">

                                        </span>
                                    )}
                                </div>


                                <button
                                    className="btn-base btn-save"
                                    onClick={onSave}
                                    disabled={loading || vodLoading || !canSaveAuIncome || noVerifiedAssets}
                                >
                                    Calculate & Save
                                </button>
                            </>
                        )}
                        {/* Blocked message */}
                        {/* {showBlockedScreen && (
                            <div className="text-sm text-red-600">
                                {onBlockedMessage}
                            </div>
                        )} */}
                    </div>

                </div>

                {/* If blocked, do not load page content — show only explanatory message */}
                {showBlockedScreen ? (
                    <div className="border rounded-lg bg-white p-6">

                        <div className="text-sm text-red-700 space-y-2">
                            <div className="font-medium">
                                {onBlockedMessage || "Asset Depletion is not allowed for this loan program."}
                            </div>

                            {/* Show additional info ONLY when blocked by loan program (not when empty) */}
                            {onBlockedMessage === "Asset Depletion is not allowed for this loan program." && (
                                <div className="text-xs text-gray-800">
                                    <div>
                                        <b>Loan Program Code:</b>{" "}
                                        {loanProgramCode || "N/A"}
                                    </div>
                                    <div>
                                        <b>Loan Product Name:</b>{" "}
                                        {loanProductName || "N/A"}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (

                    <>
                        {/* Funds Needed section */}
                        <div className="border rounded-lg bg-white">
                            <div className="section-header">Funds Needed</div>

                            <div className="p-4 space-y-4 rr-summary-card">
                                <div className="form-grid-4">
                                    {/* 1) Loan Program */}
                                    <div>
                                        <label className="form-label">Loan Program</label>
                                        <input
                                            className="form-input w-full text-sm border rounded p-1"
                                            readOnly
                                            value={fieldValues.loanProgram ?? ""}
                                        />
                                    </div>

                                    {/* 2) Assets - 1547 */}
                                    <div>
                                        <label className="form-label">Assets</label>
                                        <input
                                            className="form-input w-full text-sm border rounded p-1"
                                            readOnly
                                            value={fieldValues.assets1547 ? `$ ${money(fieldValues.assets1547)}` : ""}
                                        />
                                    </div>

                                    {/* 3) Cash From/To Borrower - 142 */}
                                    <div>
                                        <label className="form-label">Cash From/To Borrower</label>
                                        <input
                                            className="form-input w-full text-sm border rounded p-1"
                                            readOnly
                                            value={
                                                fieldValues.cashFromToBorrower142
                                                    ? `$ ${money(fieldValues.cashFromToBorrower142)}`
                                                    : ""
                                            }
                                        />
                                    </div>

                                    {/* 4) (P)ITIA - 912 */}
                                    <div>
                                        <label className="form-label">(P)ITIA</label>
                                        <input
                                            className="form-input w-full text-sm border rounded p-1"
                                            readOnly
                                            value={fieldValues.qualifyingPitiaPayment ? `$ ${money(fieldValues.qualifyingPitiaPayment)}` : ""}
                                        />
                                    </div>

                                    {/* 5) Verified Assets - CX.VERIFIED.ASSET */}
                                    <div>
                                        <label className="form-label">Verified Assets</label>
                                        <input
                                            className="form-input w-full text-sm border rounded p-1"
                                            readOnly
                                            value={fieldValues.verifiedAsset ? `$ ${money(fieldValues.verifiedAsset)}` : ""}
                                        />
                                    </div>

                                    {/* 6) Verified Asset for Reserves - CX.VERIFIED.ASSET.RESERVE */}
                                    <div>
                                        <label className="form-label">Verified Asset for Reserves</label>
                                        <input
                                            className="form-input w-full text-sm border rounded p-1"
                                            readOnly
                                            value={
                                                fieldValues.verifiedAssetReserve
                                                    ? `$ ${money(fieldValues.verifiedAssetReserve)}`
                                                    : ""
                                            }
                                        />
                                    </div>

                                    <div>
                                        <label className="form-label">Borr Funds to Close - Mos Resrv</label>
                                        <input
                                            className="form-input w-full text-sm border rounded p-1"
                                            readOnly
                                            value={fieldValues.monthsReservesRequired}
                                        />
                                    </div>


                                </div>
                            </div>
                        </div>


                        {/* Asset Utilization Grid */}
                        <div className="border rounded-lg bg-white">
                            <div className="section-header flex items-center justify-between">
                                <div>Eligible Assets</div>
                            </div>

                            <div className="p-4">
                                {vodLoading ? (
                                    <div className="flex justify-center items-center h-40">
                                        <Spin size="large" />
                                    </div>
                                ) : (
                                    <div>
                                        <div className="mb-3">
                                            <label className="form-label text-sm mb-1 block">Select Borrower Pair</label>

                                            <select
                                                className="form-input text-sm border rounded px-2 py-1 borrower-pair-select"
                                                value={selectedPairLegacyId}
                                                onChange={async (e) => {
                                                    const legacyId = e.target.value;
                                                    setSelectedPairLegacyId(legacyId);

                                                    const selected = borrowerPairs.find((p) => p.legacyId === legacyId);
                                                    setSelectedPair(selected || null);

                                                    const appId = selected?.id || "";
                                                    setApplicationId(appId);

                                                    const ownerAgeYearsMap = getOwnerAgeYearsMap(selected);
                                                    await loadVodsForApplication(token, loanId, appId, ownerAgeYearsMap);
                                                }}
                                                disabled={vodLoading || loading || !borrowerPairs.length}
                                            >
                                                {borrowerPairs.map((pair) => (
                                                    <option key={pair.legacyId} value={pair.legacyId}>
                                                        {formatPairLabel(pair)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="rr-table-wrap">
                                            <Table
                                                size="small"
                                                columns={columns}
                                                dataSource={groupedVodRows}
                                                pagination={false}
                                                bordered
                                                tableLayout="fixed"
                                                locale={{
                                                    emptyText: noVerifiedAssets
                                                        ? emptyVerifiedMessage
                                                        : <Empty />
                                                }}
                                                expandable={{
                                                    defaultExpandAllRows: true,
                                                    expandIcon: () => null
                                                }}
                                                rowClassName={(record) =>
                                                    record.isGroup
                                                        ? "asset-group-row"
                                                        : record.isGroupSubtotal
                                                            ? "asset-subtotal-row"
                                                            : record.isLastInGroup
                                                                ? "group-end-row"
                                                                : ""
                                                }

                                            />
                                        </div>

                                        {/* Totals row aligned with table columns */}
                                        <div className="rr-summary-card asset-utilization-total asset-utilization-total-border mt-1">
                                            <div className="asset-utilization-total-cell" style={{ gridColumn: "1 / span 3" }} />
                                            <div
                                                className="asset-utilization-total-cell text-left font-medium"
                                                style={{ gridColumn: "4" }}
                                            >
                                                Total
                                            </div>

                                            <div className="asset-utilization-total-cell text-left" style={{ gridColumn: "5" }}>
                                                {`$ ${money(totalCashValue)}`}
                                            </div>

                                            <div className="asset-utilization-total-cell" style={{ gridColumn: "6" }} />

                                            <div className="asset-utilization-total-cell text-left" style={{ gridColumn: "7" }}>
                                                {`$ ${money(totalAmountUsed)}`}
                                            </div>
                                        </div>

                                        <div className="rr-summary-card  asset-utilization-total asset-utilization-total-border mt-1">
                                            <div className="asset-utilization-total-cell" style={{ gridColumn: "1 / span 4" }} />

                                            <div className="asset-utilization-total-cell text-left" style={{ gridColumn: "5 / span 2" }}>
                                                Amount used by UW for Asset Depletion
                                            </div>

                                            <div className="asset-utilization-total-cell text-left" style={{ gridColumn: "7" }}>
                                                {`$ ${money(totalAmountUsedForAus)}`}
                                            </div>
                                        </div>

                                        <div className="rr-summary-card  asset-utilization-total asset-utilization-total-border mt-1">
                                            <div className="asset-utilization-total-cell" style={{ gridColumn: "1 / span 4" }} />

                                            <div className="asset-utilization-total-cell text-left" style={{ gridColumn: "5 / span 2" }}>
                                                AU Monthly Income
                                            </div>

                                            <div className="asset-utilization-total-cell text-left" style={{ gridColumn: "7" }}>
                                                {`$ ${money(auMonthlyIncome)}`}
                                            </div>
                                        </div>

                                        {/* DSCR values (DSCR programs only) */}
                                        {showDscrSection ? (
                                            <>
                                                <div className="rr-summary-card asset-utilization-total asset-utilization-total-border mt-1">
                                                    <div className="asset-utilization-total-cell" style={{ gridColumn: "1 / span 4" }} />

                                                    <div className="asset-utilization-total-cell text-left" style={{ gridColumn: "5 / span 2" }}>
                                                        DSCR Ratio
                                                    </div>

                                                    <div
                                                        className={`asset-utilization-total-cell text-left ${dscrRatioIsBad ? "text-red-600 font-semibold" : ""}`}
                                                        style={{ gridColumn: "7" }}
                                                    >
                                                        {dscrRatio ? Number(dscrRatio).toFixed(3) : ""}
                                                    </div>
                                                </div>

                                                <div className="rr-summary-card asset-utilization-total asset-utilization-total-border mt-1">
                                                    <div className="asset-utilization-total-cell" style={{ gridColumn: "1 / span 4" }} />

                                                    <div className="asset-utilization-total-cell text-left" style={{ gridColumn: "5 / span 2" }}>
                                                        DSCR Ratio w/Asset Depletion
                                                    </div>

                                                    <div
                                                        className={`asset-utilization-total-cell text-left ${dscrWithADIsBad ? "text-red-600 font-semibold" : ""}`}
                                                        style={{ gridColumn: "7" }}
                                                    >
                                                        {dscrWithAssetDeplClient ? Number(dscrWithAssetDeplClient).toFixed(3) : ""}
                                                    </div>
                                                </div>
                                            </>
                                        ) : null}


                                    </div>
                                )}
                            </div>
                        </div>

                        {/* bottom Save */}
                        <div className="flex items-center justify-end gap-3 w-full">

                            {/* Remove AU Income moved before text */}
                            <button
                                className="btn-base btn-secondary"
                                onClick={() => setShowRemoveAuConfirm(true)}
                                disabled={loading || vodLoading || showBlockedScreen || !canSaveAuIncome}
                            >
                                Remove AU Income
                            </button>

                            <div className="save-hint">
                                {canSaveAuIncome ? (
                                    <>
                                        Click <b>Calculate &amp; Save</b> to store <b>AU Monthly Income</b>
                                    </>
                                ) : (
                                    <span className="text-red-600"></span>
                                )}
                            </div>

                            {onBlockedMessage ? (
                                <div className="text-sm text-red-600">
                                    {onBlockedMessage}
                                </div>
                            ) : null}

                            <button
                                className="btn-base btn-save"
                                onClick={onSave}
                                disabled={loading || vodLoading || showBlockedScreen || !canSaveAuIncome || noVerifiedAssets}
                            >
                                Calculate & Save
                            </button>

                        </div>
                    </>
                )}

                <ConfirmPrompt
                    visible={showRemoveAuConfirm}
                    title="Confirm Removal"
                    message="Are you sure you want to remove Asset Depletion income from this loan file?"
                    confirmText="Confirm"
                    cancelText="Cancel"
                    onCancel={() => setShowRemoveAuConfirm(false)}
                    onConfirm={async () => {
                        setShowRemoveAuConfirm(false);

                        // 1) Generate + upload PDF BEFORE removing AU income
                        await handleGenerateAssetDepletionPdf();

                        // 2) Now remove AU income + clear fields
                        await handleRemoveAdIncome();
                    }}
                />

                <Toast
                    visible={toastVisible}
                    type={toastType}
                    message={toastMessage}
                    onClose={() => setToastVisible(false)}
                    animationClass={toastAnimation}
                />
            </div>
        </div>
    );
}